import RPC from '@exodus/json-rpc';
import solanaApi from '@exodus/solana-api';
import {
  createSolanaDepsFactory,
  registerRPCHandlers,
} from '@exodus/web3-solana/rpc';
import type { WalletAccountData } from '@exodus/web3-types';

import { createAppDeps, createSignerDeps, createWalletDeps } from './deps.js';
import { SIMULATION_API_URL } from '../constants.js';
import type { Web3Dependencies } from '../types.js';
import { createTransport } from '../utils/transport.js';

type SolanaDependecies = Parameters<typeof registerRPCHandlers>[0]['deps'];

const ASSET_NAME = 'solana';

const createSolanaDeps = (exodus: Web3Dependencies): SolanaDependecies => {
  const createSolanaDeps = createSolanaDepsFactory({
    // Not used in the wallet, so it's safe to pass a dummy implementation.
    assetClientInterface: {
      getFeeConfig: () =>
        Promise.resolve({ fee: getAsset(ASSET_NAME).currency.ZERO }),
    },
  });

  const getAsset = (assetName: string) =>
    exodus.assetsModule.getAsset(assetName);

  const getWalletAccountsData = (): Promise<
    Record<string, WalletAccountData>
  > => {
    return exodus.walletAccounts.getEnabled();
  };

  const getActiveWalletAccountData = async () => {
    const walletAccounts = await getWalletAccountsData();
    const activeWalletAccount = await exodus.walletAccounts.getActive();
    return walletAccounts[activeWalletAccount];
  };

  const getAddress = async (assetName: string) => {
    const walletAccount = await getActiveWalletAccountData();
    const { address } = await exodus.addressProvider.getReceiveAddress({
      assetName,
      walletAccount,
    });
    return address;
  };

  const getAddressByWalletAccount = async (
    assetName: string,
    walletAccountName: string,
  ) => {
    const walletAccounts = await getWalletAccountsData();
    const walletAccount = walletAccounts[walletAccountName];
    const { address } = await exodus.addressProvider.getReceiveAddress({
      assetName,
      walletAccount,
    });
    return address;
  };

  const sendRawTransaction = (assetName, rawTransaction, ...args) => {
    const asset = exodus.assetsModule.getAsset(assetName);
    return asset.baseAsset.api.broadcastTx(rawTransaction, ...args);
  };

  const { transactionSigner, messageSigner } = createSignerDeps({
    assetName: ASSET_NAME,
    transactionSigner: exodus.transactionSigner,
    messageSigner: exodus.messageSigner,
    analytics: exodus.analytics,
    serializeTransaction: (signedTransaction) => ({
      ...signedTransaction,
      rawTx: signedTransaction.rawTx.toString('base64'),
    }),
  });

  const solanaDeps = createSolanaDeps({
    getAsset,
    getActiveWalletAccountData,
    getAddress,
    getAddressByWalletAccount,
    getRecentBlockHash: solanaApi.getRecentBlockHash,
    getWalletAccountsData,
    sendRawTransaction,
    simulationAPIBaseURL: SIMULATION_API_URL,
    transactionSigner,
    messageSigner,
  });

  return {
    solana: solanaDeps,
    app: createAppDeps(exodus),
    wallet: createWalletDeps(exodus),
  };
};

export const createSolanaRequestHandler = (exodus: Web3Dependencies) => {
  const transport = createTransport();

  const deps = createSolanaDeps(exodus);

  registerRPCHandlers({ deps, transport, RPC });

  return (request: unknown) => {
    transport.emit('data', request);
  };
};
